/**
 * Bộ chọn màu thương hiệu: bảng màu soạn sẵn + màu tuỳ chọn + màu gần đây, và (tuỳ chọn) gradient
 * tuyến tính 2–4 điểm với góc. Giá trị ra: '#rrggbb' hoặc 'linear-gradient(<góc>deg, #màu p%, …)' —
 * đúng dạng backend chấp nhận (whitelabel/mau.ts). '' = dùng mặc định.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

// Bảng màu: 12 tông × 6 sắc độ (400→900) + dải trung tính.
const TONG: Array<[string, string[]]> = [
  ['Đỏ', ['#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d']],
  ['Cam', ['#fb923c', '#f97316', '#ea580c', '#c2410c', '#9a3412', '#7c2d12']],
  ['Hổ phách', ['#fbbf24', '#f59e0b', '#d97706', '#b45309', '#92400e', '#78350f']],
  ['Chanh', ['#a3e635', '#84cc16', '#65a30d', '#4d7c0f', '#3f6212', '#365314']],
  ['Lục', ['#4ade80', '#22c55e', '#16a34a', '#15803d', '#166534', '#14532d']],
  ['Ngọc', ['#34d399', '#10b981', '#059669', '#047857', '#065f46', '#064e3b']],
  ['Xanh két', ['#2dd4bf', '#14b8a6', '#0d9488', '#0f766e', '#115e59', '#134e4a']],
  ['Lam trời', ['#38bdf8', '#0ea5e9', '#0284c7', '#0369a1', '#075985', '#0c4a6e']],
  ['Lam', ['#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a']],
  ['Chàm', ['#818cf8', '#6366f1', '#4f46e5', '#4338ca', '#3730a3', '#312e81']],
  ['Tím', ['#c084fc', '#a855f7', '#9333ea', '#7e22ce', '#6b21a8', '#581c87']],
  ['Hồng', ['#f472b6', '#ec4899', '#db2777', '#be185d', '#9d174d', '#831843']],
]
const TRUNG_TINH = ['#ffffff', '#f1f5f9', '#cbd5e1', '#94a3b8', '#64748b', '#475569', '#334155', '#1e293b', '#172033', '#0f172a', '#020617', '#000000']

const GRADIENT_MAU = [
  'linear-gradient(135deg, #0ea5e9, #6366f1)', 'linear-gradient(135deg, #10b981, #0ea5e9)', 'linear-gradient(135deg, #f97316, #ec4899)',
  'linear-gradient(135deg, #8b5cf6, #ec4899)', 'linear-gradient(90deg, #0f172a, #1e3a8a)', 'linear-gradient(90deg, #064e3b, #0f766e)',
  'linear-gradient(90deg, #7f1d1d, #b91c1c)', 'linear-gradient(135deg, #f59e0b, #ef4444)', 'linear-gradient(135deg, #22c55e, #84cc16)',
  'linear-gradient(90deg, #312e81, #7e22ce, #db2777)', 'linear-gradient(135deg, #0c4a6e, #0e7490, #14b8a6)', 'linear-gradient(90deg, #1e293b, #475569)',
]

const RE_HEX = /^#[0-9a-f]{6}$/i
export const laGradient = (v: string | null | undefined): boolean => !!v && v.trim().toLowerCase().startsWith('linear-gradient(')
/** Màu đơn đại diện (gradient → điểm đầu). */
export function mauDau(v: string | null | undefined): string | null {
  const m = v ? /#[0-9a-f]{6}/i.exec(v) : null
  return m ? m[0].toLowerCase() : null
}

interface Diem { mau: string; viTri: number }
function tachGradient(v: string): { goc: number; diem: Diem[] } | null {
  const m = /^linear-gradient\(\s*(\d{1,3})deg\s*,(.+)\)$/i.exec(v.trim())
  if (!m) return null
  const ds = m[2].split(',').map((x) => x.trim())
  const diem = ds.map((d, i) => {
    const x = /^(#[0-9a-f]{6})(?:\s+(\d{1,3})%)?$/i.exec(d)
    return x ? { mau: x[1].toLowerCase(), viTri: x[2] !== undefined ? Number(x[2]) : Math.round((i / (ds.length - 1)) * 100) } : null
  })
  return diem.every(Boolean) ? { goc: Number(m[1]), diem: diem as Diem[] } : null
}
const ghepGradient = (goc: number, diem: Diem[]) => `linear-gradient(${goc}deg, ${diem.map((d) => `${d.mau} ${d.viTri}%`).join(', ')})`

const KHOA_GAN_DAY = 'chon-mau-gan-day'
function docGanDay(): string[] {
  try { const v = JSON.parse(localStorage.getItem(KHOA_GAN_DAY) ?? '[]'); return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, 12) : [] } catch { return [] }
}
function luuGanDay(v: string) {
  try { localStorage.setItem(KHOA_GAN_DAY, JSON.stringify([v, ...docGanDay().filter((x) => x !== v)].slice(0, 12))) } catch { /* trình duyệt chặn lưu */ }
}

function OMau({ mau, chon, onClick, nhan }: { mau: string; chon?: boolean; onClick: () => void; nhan?: string }) {
  return (
    <button type="button" title={nhan ?? mau} aria-label={nhan ?? mau} onClick={onClick}
      className={cn('relative h-6 w-6 rounded-md border border-black/10 transition-transform hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary', chon && 'ring-2 ring-primary ring-offset-1')}
      style={{ background: mau }}>
      {chon && <Check className="absolute inset-0 m-auto h-3.5 w-3.5 text-white mix-blend-difference" />}
    </button>
  )
}

export function ChonMau({ id, value, onChange, choPhepGradient = true, macDinh }: {
  id?: string
  value: string
  onChange: (v: string) => void
  choPhepGradient?: boolean
  /** Mô tả khi để trống, vd "Màu mặc định của hệ thống". */
  macDinh?: string
}) {
  const [mo, setMo] = useState(false)
  const [tab, setTab] = useState<'don' | 'gradient'>(laGradient(value) ? 'gradient' : 'don')
  const khung = useRef<HTMLDivElement>(null)
  const nut = useRef<HTMLButtonElement>(null)
  const bang = useRef<HTMLDivElement>(null)
  const [viTri, setViTri] = useState<{ top: number; left: number; maxH: number } | null>(null)
  const gd = tachGradient(value)
  const [goc, setGoc] = useState(gd?.goc ?? 135)
  const [diem, setDiem] = useState<Diem[]>(gd?.diem ?? [{ mau: mauDau(value) ?? '#0ea5e9', viTri: 0 }, { mau: '#6366f1', viTri: 100 }])
  const [diemChon, setDiemChon] = useState(0)
  const ganDay = mo ? docGanDay() : []

  // Khung nổi (portal, position fixed): mở xuống nếu đủ chỗ, không thì lật lên; cao quá màn hình thì cuộn.
  useLayoutEffect(() => {
    if (!mo) return
    const tinh = () => {
      const r = nut.current?.getBoundingClientRect()
      if (!r) return
      const cao = bang.current?.offsetHeight ?? 480
      const rong = 320, le = 8
      const duoi = window.innerHeight - r.bottom - le
      const tren = r.top - le
      const moXuong = duoi >= cao || duoi >= tren
      const maxH = Math.max(200, (moXuong ? duoi : tren) - 4)
      const top = moXuong ? r.bottom + 4 : Math.max(le, r.top - 4 - Math.min(cao, maxH))
      const left = Math.min(Math.max(le, r.left), window.innerWidth - rong - le)
      setViTri({ top, left, maxH })
    }
    tinh()
    window.addEventListener('resize', tinh)
    window.addEventListener('scroll', tinh, true)
    return () => { window.removeEventListener('resize', tinh); window.removeEventListener('scroll', tinh, true) }
  }, [mo, tab, diem.length])

  useEffect(() => {
    if (!mo) return
    const dong = (e: MouseEvent) => {
      const t = e.target as Node
      if (khung.current?.contains(t) || bang.current?.contains(t)) return
      setMo(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMo(false) }
    document.addEventListener('mousedown', dong)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', dong); document.removeEventListener('keydown', esc) }
  }, [mo])

  const chonDon = (m: string) => { onChange(m); luuGanDay(m) }
  const capNhatDiem = (ds: Diem[], g = goc) => { setDiem(ds); const v = ghepGradient(g, ds); onChange(v) }
  const datMauDiem = (m: string) => capNhatDiem(diem.map((d, i) => (i === diemChon ? { ...d, mau: m } : d)))
  const chonMau = (m: string) => (tab === 'gradient' ? datMauDiem(m) : chonDon(m))
  const dangChon = (m: string) => (tab === 'gradient' ? diem[diemChon]?.mau === m : value.toLowerCase() === m)

  return (
    <div ref={khung} className="relative">
      <button ref={nut} id={id} type="button" onClick={() => setMo((x) => !x)} aria-expanded={mo}
        className="flex h-9 w-full min-w-[180px] items-center gap-2 rounded-md border bg-background px-2 text-left text-sm hover:bg-accent/40">
        <span className="h-6 w-10 shrink-0 rounded border border-black/10"
          style={{ background: value || 'repeating-conic-gradient(#cbd5e1 0% 25%, #ffffff 0% 50%) 50% / 10px 10px' }} />
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{value ? (laGradient(value) ? 'Gradient' : value) : (macDinh ?? 'Mặc định')}</span>
      </button>

      {mo && createPortal(
        <div ref={bang} role="dialog" aria-label="Chọn màu"
          className="fixed z-[100] w-[320px] space-y-3 overflow-y-auto rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg"
          style={{ top: viTri?.top ?? -9999, left: viTri?.left ?? -9999, maxHeight: viTri?.maxH }}>
          {choPhepGradient && (
            <div className="grid grid-cols-2 gap-1 rounded-md bg-muted p-0.5 text-xs">
              {(['don', 'gradient'] as const).map((t) => (
                <button key={t} type="button" onClick={() => { setTab(t); if (t === 'gradient') capNhatDiem(diem); else if (laGradient(value)) chonDon(diem[0].mau) }}
                  className={cn('rounded px-2 py-1 font-medium', tab === t ? 'bg-background shadow-sm' : 'text-muted-foreground')}>{t === 'don' ? 'Màu đơn' : 'Gradient'}</button>
              ))}
            </div>
          )}

          {tab === 'gradient' && (
            <div className="space-y-2">
              <div className="h-10 rounded-md border border-black/10" style={{ background: ghepGradient(goc, diem) }} />
              <div className="flex items-center gap-1.5">
                {diem.map((d, i) => (
                  <div key={i} className="relative">
                    <OMau mau={d.mau} chon={i === diemChon} nhan={`Điểm ${i + 1}`} onClick={() => setDiemChon(i)} />
                    {diem.length > 2 && i === diemChon && (
                      <button type="button" aria-label="Bỏ điểm" className="absolute -right-1.5 -top-1.5 rounded-full bg-background shadow" onClick={() => { const ds = diem.filter((_, k) => k !== i).map((x, k, a) => ({ ...x, viTri: Math.round((k / (a.length - 1)) * 100) })); setDiemChon(0); capNhatDiem(ds) }}><X className="h-3 w-3" /></button>
                    )}
                  </div>
                ))}
                {diem.length < 4 && (
                  <button type="button" aria-label="Thêm điểm màu" className="flex h-6 w-6 items-center justify-center rounded-md border border-dashed hover:bg-accent"
                    onClick={() => { const ds = [...diem, { mau: diem[diem.length - 1].mau, viTri: 100 }].map((x, k, a) => ({ ...x, viTri: Math.round((k / (a.length - 1)) * 100) })); setDiemChon(ds.length - 1); capNhatDiem(ds) }}><Plus className="h-3 w-3" /></button>
                )}
                <label className="ml-auto flex items-center gap-1 text-xs text-muted-foreground" htmlFor={`${id ?? 'mau'}-goc`}>
                  Góc
                  <input id={`${id ?? 'mau'}-goc`} type="range" min={0} max={360} step={15} value={goc} onChange={(e) => { const g = Number(e.target.value); setGoc(g); capNhatDiem(diem, g) }} className="w-20 accent-primary" />
                  <span className="w-8 tabular-nums">{goc}°</span>
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground">Chọn một điểm rồi bấm màu bên dưới. Mẫu có sẵn:</p>
              <div className="grid grid-cols-6 gap-1.5">
                {GRADIENT_MAU.map((g) => (
                  <button key={g} type="button" aria-label="Gradient mẫu" onClick={() => { const t = tachGradient(g)!; setGoc(t.goc); setDiemChon(0); capNhatDiem(t.diem, t.goc) }}
                    className="h-6 rounded-md border border-black/10 hover:scale-105" style={{ background: g }} />
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <div className="grid grid-cols-12 gap-1">
              {TONG.flatMap(([ten, ds]) => ds.map((m, i) => ({ m, ten, i }))).sort((a, b) => a.i - b.i).map(({ m, ten }) => (
                <OMau key={m} mau={m} nhan={`${ten} ${m}`} chon={dangChon(m)} onClick={() => chonMau(m)} />
              ))}
            </div>
            <div className="grid grid-cols-12 gap-1 pt-1">
              {TRUNG_TINH.map((m) => <OMau key={m} mau={m} chon={dangChon(m)} onClick={() => chonMau(m)} />)}
            </div>
          </div>

          {ganDay.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] text-muted-foreground">Gần đây</p>
              <div className="flex flex-wrap gap-1">{ganDay.filter((m) => RE_HEX.test(m)).map((m) => <OMau key={m} mau={m} chon={dangChon(m)} onClick={() => chonMau(m)} />)}</div>
            </div>
          )}

          <div className="flex items-center gap-2 border-t pt-2">
            <label htmlFor={`${id ?? 'mau'}-tu-chon`} className="flex cursor-pointer items-center gap-1.5 text-xs">
              <input id={`${id ?? 'mau'}-tu-chon`} type="color" value={(tab === 'gradient' ? diem[diemChon]?.mau : mauDau(value)) ?? '#0ea5e9'}
                onChange={(e) => chonMau(e.target.value)} className="h-6 w-8 cursor-pointer rounded border bg-transparent p-0" />
              Màu khác…
            </label>
            <Button type="button" size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={() => { onChange(''); setMo(false) }}>Về mặc định</Button>
            <Button type="button" size="sm" className="h-7 text-xs" onClick={() => setMo(false)}>Xong</Button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
